import React, { ReactElement, useEffect, useRef } from "react";
import IconFascist from "../assets/player-icon-fascist.png";
import IconLiberal from "../assets/player-icon-liberal.png";
import "./GameScreen.css";

type PolicyTrackProps = {
  numLiberalPolicies: number;
  numFascistPolicies: number;
  playerCount: number;
  /** True while electing Hitler would end the game (3+ Fascist policies). */
  hitlerZone: boolean;
};

/** Presidential powers on each Fascist slot (1-6), per the board used for the player count. */
function fascistSlotLabels(playerCount: number): string[] {
  if (playerCount <= 6) {
    return ["", "", "Peek", "Execute", "Execute + veto", "Fascists win"];
  } else if (playerCount <= 8) {
    return ["", "Investigate", "Special election", "Execute", "Execute + veto", "Fascists win"];
  }
  return ["Investigate", "Investigate", "Special election", "Execute", "Execute + veto", "Fascists win"];
}

/**
 * The two policy tracks drawn as tiles on a parchment card. Newly enacted policies drop into place.
 */
export default function PolicyTrack(props: PolicyTrackProps): ReactElement {
  // Remember the previous counts so that only newly enacted tiles animate.
  const previous = useRef({ lib: props.numLiberalPolicies, fas: props.numFascistPolicies });
  const prevLib = previous.current.lib;
  const prevFas = previous.current.fas;
  useEffect(() => {
    previous.current = { lib: props.numLiberalPolicies, fas: props.numFascistPolicies };
  });

  const liberalSlots = [0, 1, 2, 3, 4].map((i) => {
    if (i < props.numLiberalPolicies) {
      return (
        <div key={i} className={"pt-tile pt-liberal" + (i >= prevLib ? " pt-new" : "")}>
          <img className="pt-emblem" src={IconLiberal} alt="Liberal policy" draggable={false} />
        </div>
      );
    }
    return (
      <div key={i} className="pt-slot pt-slot-liberal">
        {i === 4 ? "Liberals win" : ""}
      </div>
    );
  });

  const labels = fascistSlotLabels(props.playerCount);
  const fascistSlots = labels.map((label, i) => {
    if (i < props.numFascistPolicies) {
      return (
        <div key={i} className={"pt-tile pt-fascist" + (i >= prevFas ? " pt-new" : "")}>
          <img className="pt-emblem" src={IconFascist} alt="Fascist policy" draggable={false} />
        </div>
      );
    }
    const inZone = i >= 3 && i < 5;
    const glow = props.hitlerZone && inZone ? " pt-zone-glow" : "";
    return (
      <div
        key={i}
        className={"pt-slot pt-slot-fascist" + (inZone ? " pt-zone" : "") + (i === 5 ? " pt-final" : "") + glow}
        style={glow ? { animationDelay: (i - 3) * 0.3 + "s" } : undefined}
      >
        {label}
      </div>
    );
  });

  return (
    <div className="pt-card">
      <div className="pt-track">
        <div className="pt-track-header">
          <div className="pt-track-title pt-title-liberal">Liberal policies</div>
          <div className="pt-count">{props.numLiberalPolicies} of 5</div>
        </div>
        <div className="pt-row">{liberalSlots}</div>
      </div>
      <div className="pt-track">
        <div className="pt-track-header">
          <div className="pt-track-title pt-title-fascist">Fascist policies</div>
          <div className="pt-count">{props.numFascistPolicies} of 6</div>
        </div>
        <div className="pt-row">{fascistSlots}</div>
        <div className={"pt-zone-caption" + (props.numFascistPolicies >= 3 ? " pt-zone-caption-live" : "")}>
          {props.numFascistPolicies >= 3
            ? "Hitler zone: electing Hitler as Chancellor now wins it for the Fascists"
            : "From the 3rd Fascist policy, electing Hitler as Chancellor wins it for the Fascists"}
        </div>
      </div>
    </div>
  );
}
